import { ApiProductCard } from '../../api';
import ProductItemListEvent from './productItemListEvent';
import {
  ApiBestPricePackageModel,
  ApiBooking,
  ApiCustomer,
  ApiItem,
  ApiItemType,
  ApiPrice,
  ApiPriceModifierType
} from '@ibe/api';
import dayjs from 'dayjs';
import { PREVIOUS_ITEM_LIST_ID, PREVIOUS_ITEM_LIST_NAME } from '../Util/sessionStorageHelper';
import { TrackingEvents } from './events';
import { TravelDetailsEvent } from '@ibe/services';
import { eCommerceStorage } from '../Pages/Checkout/Components/ExtendedBooking/ContextAndProvider';

type DataLayer = {
  event: string;
  ecommerce: Ecommerce;
  step?: string;
};
type WindowWithDataLayer = Window & {
  dataLayer: DataLayer[];
};

const PackageTypes = {
  PFLUG: 'PFLUG',
  PBUS: 'PBUS'
};

declare const window: WindowWithDataLayer;

class TrackingService {
  private readonly itemBrandName = 'trendtours';

  private readonly affiliationName = this.itemBrandName + ' Online Store';

  private readonly daysStr = ' Tage';

  private readonly travelYearStr = 'Reisejahr ';

  private readonly wkzPartner = 'WKZ_PARTNER';

  trackProductListView(payload: ProductItemListEvent, eventName: string): void {
    const data = this.mapProductsToItems(payload.items);
    this.pushToWindowDataLayer(eventName, data);
  }

  trackPackageListView(payload: ApiBestPricePackageModel[], eventName: string): void {
    const previousItemListId =
      typeof window !== 'undefined' ? sessionStorage.getItem(PREVIOUS_ITEM_LIST_ID) : '';
    const previousItemListName =
      typeof window !== 'undefined' ? sessionStorage.getItem(PREVIOUS_ITEM_LIST_NAME) : '';

    const items: Item[] = payload.map((it, index) => ({
      ...this.mapItem(it, index),
      item_list_id: previousItemListId ?? '',
      item_list_name: previousItemListName ?? ''
    }));

    const data: Ecommerce = {
      item_list_id: previousItemListId ?? '',
      item_list_name: previousItemListName ?? '',
      items
    };
    this.pushToWindowDataLayer(eventName, data);
  }

  trackItem(payload: ApiItem, eventName: string): void {
    const item = this.mapItem(payload);
    const data: Ecommerce = {
      currency: payload.price?.currencyCode ?? 'EUR',
      value: payload.price?.finalPrice ?? 0,
      items: [item]
    };
    this.pushToWindowDataLayer(eventName, data);
  }

  beginCheckout(payload: ApiBooking | null): void {
    eCommerceStorage.clear();

    if (!payload?.bookedItems) return;

    const newBookingEcommerce = this.mapBookedItems(payload);
    this.setCheckoutStorage(newBookingEcommerce);

    const hasCheckoutStarted = this.getLatestDataLayer(TrackingEvents.BEGIN_CHECKOUT);
    if (!hasCheckoutStarted) {
      this.pushToWindowDataLayer(TrackingEvents.BEGIN_CHECKOUT, newBookingEcommerce);
    }
  }

  private mapTravelDetails(payload: TravelDetailsEvent): Ecommerce | undefined {
    if (!payload.booking?.bookedItems) return;

    const mappedBookedItemsEcommerce =
      this.buildCheckoutEcommerce(payload.booking) ?? this.mapBookedItems(payload.booking);

    const bookedItems = payload.booking.bookedItems;
    const packageItem = bookedItems.find(it => it.itemType === ApiItemType.PACKAGE);
    const flightItem = bookedItems.find(it => it.itemType === ApiItemType.FLIGHT);
    const flightDepartureAirport = this.getFlightDepartureAirportCode(flightItem);

    if (!mappedBookedItemsEcommerce.items?.[0]) {
      return mappedBookedItemsEcommerce;
    }

    mappedBookedItemsEcommerce.items = [
      {
        ...mappedBookedItemsEcommerce.items[0],
        travel_start_date: packageItem?.startDate,
        travel_end_date: packageItem?.endDate ?? payload.booking.bookedItems[0]?.endDate,
        travel_duration: payload.travel_duration,
        flight_boarding: flightDepartureAirport,
        amount_travellers: payload.booking.travelers?.length ?? 0,
        hotel: undefined,
        amount_rooms: undefined,
        hotel_meals: undefined
      }
    ];

    return mappedBookedItemsEcommerce;
  }

  addTravelDetails(payload: TravelDetailsEvent, step?: string): void {
    const latestEvent = this.getLatestDataLayer(TrackingEvents.ADD_TRAVEL_DETAILS);
    const mappedTravelDetails = this.mapTravelDetails(payload);
    const latestEcommerce = latestEvent?.ecommerce
      ? this.sanitizeEventEcommerce(latestEvent.ecommerce)
      : undefined;

    const ecommerce = mappedTravelDetails ?? latestEcommerce;
    if (!ecommerce) return;

    const shouldPush =
      !latestEcommerce ||
      !this.hasSameEcommerce(latestEcommerce, ecommerce) ||
      latestEvent?.step !== step;

    if (!shouldPush) return;

    this.setCheckoutStorage(ecommerce);
    this.pushToWindowDataLayer(TrackingEvents.ADD_TRAVEL_DETAILS, ecommerce, step);
  }

  checkoutStep(step?: string, booking?: ApiBooking | null): void {
    const ecommerce = this.buildCheckoutEcommerce(booking);
    if (!ecommerce) return;

    this.setCheckoutStorage(ecommerce);
    this.pushToWindowDataLayer(TrackingEvents.CHECKOUT_STEP, ecommerce, step);
  }

  additionalServices(
    service_type: string,
    service_name: string,
    additional_service: string,
    remove = false,
    booking?: ApiBooking | null
  ): void {
    const ecommerce = this.buildCheckoutEcommerce(booking);
    if (!ecommerce?.items?.[0]) return;

    const currentAdditionalServices = ecommerce.items[0].additional_services ?? [];

    const nextAdditionalServices = remove
      ? currentAdditionalServices.filter((service: string) => service !== additional_service)
      : Array.from(new Set([...currentAdditionalServices, additional_service]));

    const updatedEcommerce: Ecommerce = {
      ...ecommerce,
      service_type,
      service_name,
      items: [
        {
          ...ecommerce.items[0],
          additional_services:
            nextAdditionalServices.length > 0 ? nextAdditionalServices : undefined
        }
      ]
    };

    this.setCheckoutStorage(this.sanitizeEventEcommerce(updatedEcommerce) ?? updatedEcommerce);
    this.pushToWindowDataLayer(TrackingEvents.ADD_ADDITIONAL_SERVICES, updatedEcommerce);
  }

  purchase(payload: ApiBooking | null): void {
    if (!payload) return;

    const purchaseData =
      this.buildCheckoutEcommerce(payload) ??
      this.sanitizeEventEcommerce(this.getLatestCheckoutContextEvent()?.ecommerce) ??
      this.sanitizeEventEcommerce(eCommerceStorage.get());

    if (!purchaseData) return;

    const tax = this.sumPriceModifiers(payload.price, ApiPriceModifierType.TAX);

    if (payload.bookingNumber) {
      purchaseData.transaction_id = +payload.bookingNumber;
    }

    purchaseData.tax = tax;

    const client = payload.client as ApiCustomer | undefined;
    if (client) {
      purchaseData.first_name = client.firstName;
      purchaseData.last_name = client.lastName;
      purchaseData.postal_code = client.address.postalCode;
      purchaseData.country = client.address.countryCode;
      purchaseData.email = client.communicationDetails?.email;
      purchaseData.phone = client.communicationDetails?.phone;
    }

    this.pushToWindowDataLayer(TrackingEvents.PURCHASE, purchaseData);
  }

  failedCheckout(packageItem?: ApiBestPricePackageModel, numberOfPersons?: number): void {
    if (!packageItem) return;

    const viewItemEcommerce = this.sanitizeEventEcommerce(
      this.getLatestDataLayer(TrackingEvents.VIEW_ITEM)?.ecommerce
    );
    const baseItem = viewItemEcommerce?.items?.[0] ?? this.mapItem(packageItem);

    const previousItemListId =
      window === undefined ? '' : sessionStorage.getItem(PREVIOUS_ITEM_LIST_ID);
    const previousItemListName =
      window === undefined ? '' : sessionStorage.getItem(PREVIOUS_ITEM_LIST_NAME);

    const coupon =
      this.decodeBase64JSON<{ promotionCode?: string }>(packageItem.id)?.promotionCode ??
      baseItem.coupon ??
      '';

    const ecommerce: Ecommerce = {
      currency: packageItem.price?.currencyCode ?? viewItemEcommerce?.currency ?? 'EUR',
      value: packageItem.price?.finalPrice ?? viewItemEcommerce?.value ?? 0,
      checkout_type: 'iso',
      coupon: coupon,
      tax: this.sumPriceModifiers(packageItem.price, ApiPriceModifierType.TAX),
      items: [
        {
          ...baseItem,
          coupon: coupon,
          item_list_id: baseItem.item_list_id ?? previousItemListId ?? '',
          item_list_name: baseItem.item_list_name ?? previousItemListName ?? '',
          travel_start_date: packageItem.startDate
            ? dayjs(packageItem.startDate).format('YYYY-MM-DD')
            : baseItem.travel_start_date,
          departure_airport:
            packageItem.origin ?? packageItem.stationCodes?.[0] ?? baseItem.departure_airport,
          amount_travellers: numberOfPersons ?? baseItem.amount_travellers
        }
      ]
    };

    this.pushToWindowDataLayer(TrackingEvents.FAILED_CHECKOUT, ecommerce);
  }

  private sumPriceModifiers(price: ApiPrice | undefined, type: ApiPriceModifierType): number {
    return price?.modifiers?.reduce((sum, m) => sum + (m.type === type ? m.absolute : 0), 0) ?? 0;
  }

  private getItemCategory(typeCode?: string): string {
    switch (typeCode) {
      case 'SB':
        return 'Sonnenhits';
      case PackageTypes.PFLUG:
        return 'Flugreise';
      default:
        return '';
    }
  }

  private getSeasonName(item: Partial<ApiItem>): string | undefined {
    const seasonContent = item.metaInfo?.contentByMetaType?.season?.content as
      | { productName?: string }
      | undefined;
    const name = seasonContent?.productName;

    return name ? this.replaceHtmlAndUmlauts(name) : undefined;
  }

  private mapItem(packageItem: Partial<ApiBestPricePackageModel>, index = 0): Item {
    const code = packageItem.code;
    const name = this.getSeasonName(packageItem);

    const priceModifiers = packageItem.price?.modifiers?.find(
      pr => pr.type === ApiPriceModifierType.DISCOUNT
    );
    let coupon = '';
    const decodedId = this.decodeBase64JSON<{ promotionCode?: string }>(packageItem?.id);
    if (decodedId?.promotionCode) {
      coupon = decodedId.promotionCode;
    }

    let discount = priceModifiers?.absolute;
    discount = discount ? Math.abs(discount) : 0;

    const itemCategory = this.getItemCategory(packageItem.typeCode);
    const itemCategory2 = packageItem?.geoAssignment?.geoUnit?.name;
    const endDate = dayjs(packageItem.endDate);
    const itemCategory3 = this.travelYearStr + endDate.year();

    const duration = packageItem.duration?.duration
      ? packageItem.duration.duration + this.daysStr
      : '';
    const price = packageItem.price?.finalPrice;

    const storedWkzPartner =
      typeof window !== 'undefined' ? sessionStorage.getItem(this.wkzPartner) : '';
    return {
      item_id: code,
      item_name: name,
      affiliation: this.affiliationName,
      coupon: coupon,
      discount: discount,
      index: index,
      item_brand: this.itemBrandName,
      item_category: itemCategory,
      item_category2: itemCategory2,
      item_category3: itemCategory3,
      item_variant: duration,
      price: price,
      quantity: 1,
      wkz_partner: storedWkzPartner ?? undefined
    };
  }

  private mapPackageItem(payload: ApiBooking | null): Item | undefined {
    const bookedItems = payload?.bookedItems;
    const packageItem = bookedItems?.find(it => it.itemType === ApiItemType.PACKAGE);
    if (packageItem) {
      const item: Item = {};
      const decodedPackageId = this.decodeBase64JSON<{ packageModelId?: string }>(packageItem.id);

      if (decodedPackageId?.packageModelId) {
        const decodedPackageModelId = this.decodeBase64JSON<{
          typeCode?: string;
          code?: string;
        }>(decodedPackageId.packageModelId);
        if (decodedPackageModelId) {
          const typeCode = decodedPackageModelId.typeCode;
          item.item_id = decodedPackageModelId?.code;
          item.item_category = this.getItemCategory(typeCode);
        }
      }

      item.item_name = this.getSeasonName(packageItem);

      if (packageItem.endDate) {
        const endDate = dayjs(packageItem.endDate);
        item.item_variant = this.travelYearStr + endDate.year();
      }
      return item;
    }
  }

  private mapBookedItems(booking: ApiBooking | null): Ecommerce {
    const packageItem = this.mapPackageItem(booking);
    const coupon = booking?.promoCodes?.[0] ?? '';
    let discount = booking?.totalDiscount?.finalPrice ?? 0;
    const modifierDiscount = this.sumPriceModifiers(booking?.price, ApiPriceModifierType.DISCOUNT);

    discount = Math.abs(discount) + Math.abs(modifierDiscount);

    const previousItemListId =
      typeof window !== 'undefined' ? sessionStorage.getItem(PREVIOUS_ITEM_LIST_ID) : '';
    const previousItemListName =
      typeof window !== 'undefined' ? sessionStorage.getItem(PREVIOUS_ITEM_LIST_NAME) : '';
    const storedWkzPartner =
      typeof window !== 'undefined' ? sessionStorage.getItem(this.wkzPartner) : '';

    const finalPrice = booking?.price?.finalPrice ?? 0;
    const originalPrice = finalPrice + discount;

    const item: Item[] = [
      {
        ...packageItem,
        affiliation: this.affiliationName,
        coupon: coupon,
        discount: discount,
        index: 0,
        item_brand: this.itemBrandName,
        item_list_id: previousItemListId ?? '',
        item_list_name: previousItemListName ?? '',
        price: originalPrice,
        quantity: 1,
        wkz_partner: storedWkzPartner ?? undefined
      }
    ];

    const tax = this.sumPriceModifiers(booking?.price, ApiPriceModifierType.TAX);

    return {
      currency: booking?.price?.currencyCode ?? 'EUR',
      value: finalPrice,
      checkout_type: 'iso',
      coupon: coupon,
      items: item,
      tax: tax
    };
  }

  private mapProductsToItems(payload: ApiProductCard[]): Ecommerce {
    const previousItemListId =
      typeof window !== 'undefined' ? sessionStorage.getItem(PREVIOUS_ITEM_LIST_ID) : '';
    const previousItemListName =
      typeof window !== 'undefined' ? sessionStorage.getItem(PREVIOUS_ITEM_LIST_NAME) : '';

    return {
      items: payload?.map((card, index) => {
        const price =
          card.strikeThroughPrice && card.strikeThroughPrice.amount
            ? card.strikeThroughPrice
            : card.price;

        const name = this.replaceHtmlAndUmlauts(card.name);
        const seasonName = this.replaceHtmlAndUmlauts(card.seasonName);

        const item: Item = {
          item_id: card.code,
          item_name: name,
          affiliation: this.affiliationName,
          coupon: card.selectedPromotionCode || '',
          discount: card.selectedPromotionCode
            ? parseFloat(card.selectedPromotionValue ?? '0')
            : '',
          index,
          item_brand: this.itemBrandName,
          item_category: card.productTypes.join(', ') ?? '',
          item_category2: card.country.name ?? '',
          item_category3: seasonName ?? '',
          price: price.amount ?? 0,
          quantity: 1,
          item_list_id: previousItemListId ?? '',
          item_list_name: previousItemListName ?? '',
          wkz_partner: card?.wkzPartner ?? undefined
        };

        return item;
      })
    };
  }

  private pushToWindowDataLayer(eventName: string, data: Ecommerce, step?: string): void {
    if (!window.dataLayer) return;

    const payload = step
      ? { event: eventName, step: step, ecommerce: data }
      : { event: eventName, ecommerce: data };

    window.dataLayer.push(payload);
  }

  private getLatestDataLayer(eventName: string): DataLayer | undefined {
    if (!window.dataLayer) return;
    const existingEvents = window.dataLayer.filter(event => event.event === eventName);
    if (existingEvents.length > 0) {
      return existingEvents[existingEvents.length - 1];
    }
  }

  private getLatestCheckoutContextEvent(): DataLayer | undefined {
    if (!window.dataLayer) return;

    const relevantEvents = new Set<string>([
      TrackingEvents.ADD_ADDITIONAL_SERVICES,
      TrackingEvents.CHECKOUT_STEP,
      TrackingEvents.ADD_TRAVEL_DETAILS,
      TrackingEvents.BEGIN_CHECKOUT
    ]);

    return [...window.dataLayer].reverse().find(event => relevantEvents.has(event.event));
  }

  private buildCheckoutEcommerce(booking?: ApiBooking | null): Ecommerce | undefined {
    const latestContextEvent = this.getLatestCheckoutContextEvent();
    const latestContextEcommerce = latestContextEvent?.ecommerce
      ? this.sanitizeEventEcommerce(latestContextEvent.ecommerce)
      : undefined;

    const storedEcommerce = this.sanitizeEventEcommerce(eCommerceStorage.get());
    const contextEcommerce = latestContextEcommerce ?? storedEcommerce;

    if (booking?.bookedItems) {
      const newBookingEcommerce = this.mapBookedItems(booking);
      return this.mergeTemporaryCheckoutContext(newBookingEcommerce, contextEcommerce);
    }

    if (contextEcommerce) {
      return this.cloneDeep(contextEcommerce);
    }
  }

  // The backend gives us correct prices but the frontend holds temporary details
  // (e.g. hotel meals or extra services) - We combine both here
  // so Google Analytics gets the full iamge
  private mergeTemporaryCheckoutContext(
    baseEcommerce: Ecommerce,
    contextEcommerce?: Ecommerce
  ): Ecommerce {
    if (!contextEcommerce?.items?.[0] || !baseEcommerce.items?.[0]) {
      return this.sanitizeEventEcommerce(baseEcommerce) ?? baseEcommerce;
    }

    const contextItem = contextEcommerce.items[0];
    const mergedAdditionalServices = Array.from(
      new Set([
        ...(baseEcommerce.items[0].additional_services ?? []),
        ...(contextItem.additional_services ?? [])
      ])
    );

    const mergedEcommerce: Ecommerce = {
      ...baseEcommerce,
      checkout_type: contextEcommerce.checkout_type ?? baseEcommerce.checkout_type ?? 'iso',
      items: [
        {
          ...baseEcommerce.items[0],
          travel_start_date:
            contextItem.travel_start_date ?? baseEcommerce.items[0].travel_start_date,
          travel_end_date: contextItem.travel_end_date ?? baseEcommerce.items[0].travel_end_date,
          travel_duration: contextItem.travel_duration ?? baseEcommerce.items[0].travel_duration,
          flight_boarding: contextItem.flight_boarding ?? baseEcommerce.items[0].flight_boarding,
          amount_travellers:
            contextItem.amount_travellers ?? baseEcommerce.items[0].amount_travellers,
          hotel: contextItem.hotel ?? baseEcommerce.items[0].hotel,
          amount_rooms: contextItem.amount_rooms ?? baseEcommerce.items[0].amount_rooms,
          hotel_meals: contextItem.hotel_meals ?? baseEcommerce.items[0].hotel_meals,
          additional_services:
            mergedAdditionalServices.length > 0 ? mergedAdditionalServices : undefined,
          wkz_partner: contextItem.wkz_partner ?? baseEcommerce.items[0].wkz_partner
        }
      ]
    };

    return this.sanitizeEventEcommerce(mergedEcommerce) ?? mergedEcommerce;
  }

  private sanitizeEventEcommerce(ecommerce?: Ecommerce): Ecommerce | undefined {
    if (!ecommerce) return;

    const sanitized = this.cloneDeep(ecommerce) as Ecommerce & {
      service_name?: string;
      service_type?: string;
    };
    delete sanitized.service_name;
    delete sanitized.service_type;

    return sanitized;
  }

  private setCheckoutStorage(ecommerce: Ecommerce): void {
    eCommerceStorage.set(this.cloneDeep(ecommerce));
  }

  private hasSameEcommerce(left?: Ecommerce, right?: Ecommerce): boolean {
    if (!left || !right) return false;
    return JSON.stringify(left) === JSON.stringify(right);
  }

  private cloneDeep<T>(value: T): T {
    if (typeof structuredClone === 'function') {
      return structuredClone(value);
    }
    return JSON.parse(JSON.stringify(value)) as T;
  }

  private decodeBase64JSON<T>(encodedString?: string | null): T | undefined {
    if (!encodedString) return undefined;
    try {
      return JSON.parse(atob(encodedString)) as T;
    } catch (e) {
      console.warn('Failed to parse encoded ID in TrackingService', e);
      return undefined;
    }
  }

  private getFlightDepartureAirportCode(flightItem?: ApiItem): string | undefined {
    if (flightItem?.idParent) {
      // If the normal flight data is missing the starting airport code,
      // we have to decode 'idParent' string to find it
      const decodedIdParent = this.decodeBase64JSON<{ id?: string }>(flightItem.idParent);
      if (decodedIdParent?.id) {
        const decodedIdParentId = this.decodeBase64JSON<{ startingPointCode?: string }>(
          decodedIdParent.id
        );
        return decodedIdParentId?.startingPointCode;
      }
    }

    return undefined;
  }

  private replaceHtmlAndUmlauts(input: string) {
    if (!input) {
      return input;
    }
    return this.clearHTMLTags(input).trim();
  }

  private clearHTMLTags = (input: string) => {
    const output = new DOMParser().parseFromString(input, 'text/html');
    return output.body.textContent || '';
  };
}

export default TrackingService;
